import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { ArrowRight, MessageSquare, BookOpen } from "lucide-react";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [enrollments, setEnrollments] = useState([]);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    if (!loading && !user) nav("/login?next=/dashboard");
  }, [user, loading, nav]);

  useEffect(() => {
    if (!user) return;
    api.get("/enrollments").then((r) => setEnrollments(r.data)).catch(() => {});
    api.get("/chat/sessions").then((r) => setSessions(r.data)).catch(() => {});
  }, [user]);

  if (loading || !user) return <div className="min-h-screen bg-[#0D0C0A] pt-32 px-6 text-[#B3AFA8]">Loading…</div>;

  return (
    <main data-testid="dashboard-page" className="bg-[#0D0C0A] min-h-screen pt-32 pb-24 px-6 md:px-12">
      <div className="max-w-[1400px] mx-auto">
        <p className="label-eyebrow mb-4">Your studio</p>
        <h1 className="font-display text-5xl sm:text-6xl leading-tight mb-4">
          Good to see you, <span className="italic text-[#D4AF37]">{user.name.split(" ")[0]}</span>.
        </h1>
        <p className="text-[#D5D0C5] text-lg mb-16 max-w-xl">Here's where you left off. Pick a class, continue a conversation, or begin something new.</p>

        <section className="mb-20">
          <div className="flex items-end justify-between mb-8">
            <h2 className="font-display text-3xl sm:text-4xl">Your classes</h2>
            <Link to="/classes" className="text-sm text-[#D4AF37] hover:underline inline-flex items-center gap-2" data-testid="dash-browse-classes">
              Browse all <ArrowRight size={14} />
            </Link>
          </div>

          {enrollments.length === 0 ? (
            <div className="border border-[#332D29] bg-[#1A1715] p-12 text-center">
              <BookOpen className="mx-auto text-[#D4AF37] mb-4" size={32} />
              <p className="text-[#D5D0C5] mb-6">You haven't enrolled in any classes yet. The wheel is waiting.</p>
              <Link to="/classes" data-testid="dash-start-first" className="btn-gold px-6 py-3 rounded-full text-sm inline-flex items-center gap-2">
                Start your first class <ArrowRight size={14} />
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {enrollments.map((e) => {
                const pct = Math.round((e.completed / Math.max(e.total_lessons, 1)) * 100);
                return (
                  <Link
                    key={e.course_id}
                    to={`/classes/${e.course_id}`}
                    data-testid={`enrolled-${e.course_id}`}
                    className="group border border-[#332D29] bg-[#1A1715] hover:border-[#D4AF37]/50 transition-all overflow-hidden"
                  >
                    <div className="aspect-[4/3] overflow-hidden">
                      <img src={e.cover} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 opacity-80" />
                    </div>
                    <div className="p-6">
                      <p className="label-eyebrow mb-3">{e.level}</p>
                      <h3 className="font-display text-2xl mb-4 group-hover:text-[#D4AF37] transition-colors">{e.title}</h3>
                      <div className="h-[2px] bg-[#332D29] rounded-full overflow-hidden mb-3">
                        <div className="h-full bg-[#D4AF37] transition-all" style={{ width: `${pct}%` }} />
                      </div>
                      <p className="text-xs text-[#B3AFA8] tracking-wide">{e.completed} of {e.total_lessons} lessons · {pct}%</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>

        <section>
          <div className="flex items-end justify-between mb-8">
            <h2 className="font-display text-3xl sm:text-4xl">Your conversations</h2>
            <Link to="/chat" className="text-sm text-[#D4AF37] hover:underline inline-flex items-center gap-2" data-testid="dash-open-chat">
              Open chat <ArrowRight size={14} />
            </Link>
          </div>

          {sessions.length === 0 ? (
            <div className="border border-[#332D29] bg-[#1A1715] p-12 text-center">
              <MessageSquare className="mx-auto text-[#D4AF37] mb-4" size={32} />
              <p className="text-[#D5D0C5] mb-6">No conversations yet. Marilyn is always one message away.</p>
              <Link to="/chat" className="btn-gold px-6 py-3 rounded-full text-sm inline-flex items-center gap-2">
                Ask your first question <ArrowRight size={14} />
              </Link>
            </div>
          ) : (
            <div className="space-y-1">
              {sessions.slice(0, 6).map((s) => (
                <Link
                  key={s.id}
                  to="/chat"
                  data-testid={`dash-session-${s.id}`}
                  className="group flex items-center justify-between border-t border-[#332D29] py-5 hover:text-[#D4AF37] transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <MessageSquare size={18} className="text-[#D4AF37]/70" />
                    <span className="text-[#F5F2EB] group-hover:text-[#D4AF37] transition-colors truncate">{s.title}</span>
                  </div>
                  <ArrowRight size={16} className="text-[#B3AFA8] group-hover:text-[#D4AF37] group-hover:translate-x-1 transition-all" />
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
